import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  Folder as FolderIcon,
  Add as AddIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';

const ProjectSwitcher = () => {
  const { projects, activeProject, selectProject, loading } = useProject();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleProjectSelect = async (project) => {
    await selectProject(project);
    navigate(`/projects/${project.id}`);
    handleClose();
  };

  const handleCreateNew = () => {
    navigate('/projects/new');
    handleClose();
  };

  const handleManageProjects = () => {
    navigate('/projects');
    handleClose();
  };

  if (loading && projects.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 200 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="inherit">Loading…</Typography>
      </Box>
    );
  }

  if (!loading && projects.length === 0) {
    return (
      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateNew} size="small">
        Create Project
      </Button>
    );
  }

  const displayProject = activeProject;

  return (
    <Box>
      <Button
        onClick={handleClick}
        endIcon={<ArrowDownIcon />}
        data-tutorial-id="project-switcher"
        sx={{
          color: 'inherit',
          justifyContent: 'flex-start',
          minWidth: 200,
          maxWidth: 250,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <Avatar src={displayProject?.avatar_url} sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
            {!displayProject?.avatar_url && <FolderIcon sx={{ fontSize: 14 }} />}
          </Avatar>
          <Box sx={{ overflow: 'hidden', textAlign: 'left' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {displayProject?.name || 'Select Project'}
            </Typography>
            {displayProject && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                Project
              </Typography>
            )}
          </Box>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 280, maxWidth: 320 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" color="text.secondary">Projects</Typography>
        </Box>

        {projects.map((project) => (
          <MenuItem
            key={project.id}
            onClick={() => handleProjectSelect(project)}
            selected={project.id === displayProject?.id}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <Avatar src={project.avatar_url} sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {!project.avatar_url && <FolderIcon sx={{ fontSize: 16 }} />}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{project.name}</Typography>
              }
              secondary={
                project.description ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                  >
                    {project.description}
                  </Typography>
                ) : null
              }
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleCreateNew}>
          <ListItemIcon><AddIcon /></ListItemIcon>
          <ListItemText primary="Create Project" />
        </MenuItem>

        <MenuItem onClick={handleManageProjects}>
          <ListItemIcon><ListIcon /></ListItemIcon>
          <ListItemText primary="Manage Projects" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProjectSwitcher;
